import React from 'react';
import { Card } from '@parsina/framework';
import { ChevronDown } from 'lucide-react';
import { C } from '../../constants/theme';

// A titled panel: the kit's `Card` plus a heading row.
//
// This is COMPOSITION, not a re-implementation — `SectionCard` in the kit is a
// numbered step in a multi-step form, and `CollapsibleSection` is a disclosure.
// Neither is "a card with a title", which is what a read-only record section is.
// Everything visual still comes from the kit's Card and from tokens.
const Panel = ({
  title, action, children, style,
  /**
   * Turn the heading into a disclosure.
   *
   * The visit workspace needs this on the phone: its three persistent sections
   * are stacked in one scroll there, and three expanded ones show none of them.
   * It belongs HERE rather than in a wrapper because a `CollapsibleSection`
   * around a Panel renders the title TWICE — the wrapper's and the Panel's own.
   *
   * `collapsible` is opt-in and the default is unchanged, so every existing
   * Panel keeps its plain heading.
   */
  collapsible = false,
  defaultOpen = true,
  ...rest
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const bodyId = React.useId();
  const shown = collapsible ? open : true;

  return (
    <Card style={{ padding: 0, overflow: 'hidden', ...style }} {...rest}>
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 20px',
            borderBottom: shown ? `1px solid ${C.border}` : 'none',
          }}
        >
          {/* An <h2>, not a styled <span>. Every screen's <h1> comes from the
              kit's PageHeader/InnerPageHeader, and a page whose sections are all
              spans gives a screen reader exactly one landmark to jump to — the
              title — and no way to reach «حساسیت‌ها» or «تداخل». The type ramp is
              unchanged; only the element is.

              When collapsible, the BUTTON goes inside the heading rather than
              the heading inside a button: a role="button"'s children are
              presentational, so wrapping the other way erases the section from
              a screen reader's heading list. Same accordion shape امروز uses. */}
          {title && (
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
              {collapsible ? (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={bodyId}
                  onClick={() => setOpen((o) => !o)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    minHeight: 44, background: 'none', border: 'none',
                    padding: 0, margin: 0, font: 'inherit', color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {title}
                  <ChevronDown
                    size={16}
                    aria-hidden
                    style={{
                      transform: open ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}
                  />
                </button>
              ) : title}
            </h2>
          )}
          {action && <span style={{ marginInlineStart: 'auto' }}>{action}</span>}
        </div>
      )}
      {shown && <div id={bodyId} style={{ padding: '16px 20px' }}>{children}</div>}
    </Card>
  );
};

export default Panel;
